import type { ID, ItemSources, QuoteItem, QuotePreset, Service } from '@/types';
import { createId } from '@/utils/id';

/**
 * Bundle provenance.
 *
 * Every quote line records *where its quantity came from*: the `manual` key for
 * anything the user added or typed, plus one key per bundle that contributed.
 * The invariant is `quantity === sum(sources)`.
 *
 * That single structure answers all four questions the UI asks — which bundles
 * are active, how many lines each contributed, what to remove when a bundle is
 * dismissed, and whether the quote already matches a saved bundle — without any
 * of that logic leaking into a component.
 */

export const MANUAL_SOURCE = 'manual';

/**
 * A quote line always represents at least one unit of work.
 *
 * A line with quantity 0 is not a discount, it is a line that should have been
 * deleted — it prices at nothing, prints as a zero row on the customer's PDF and
 * makes the totals look wrong. Removal is `removeItem`; quantity is never the
 * mechanism for it.
 */
export const MIN_ITEM_QUANTITY = 1;

/**
 * The single gate every quantity passes through.
 *
 * Rejects the whole family of bad inputs in one place — 0, negatives, NaN,
 * Infinity and the `Number('')` of an emptied text field — so no caller has to
 * remember to. Fractional quantities above the minimum are legitimate: some
 * units are sold in halves.
 */
export function clampQuantity(value: number): number {
  if (!Number.isFinite(value)) return MIN_ITEM_QUANTITY;
  return Math.max(MIN_ITEM_QUANTITY, value);
}

export function sumSources(sources: ItemSources): number {
  return Object.values(sources).reduce(
    (total, value) => total + (Number.isFinite(value) ? Math.max(0, value) : 0),
    0,
  );
}

/**
 * Older snapshots predate provenance; treat their whole quantity as manual.
 *
 * This is also the healing point for persisted data that violates the minimum —
 * a draft written by an earlier build, a hand-edited localStorage entry, a
 * restored quote. Anything below the minimum is lifted back to it, and the
 * `quantity === sum(sources)` invariant is re-established rather than assumed.
 */
export function normaliseItem(item: QuoteItem): QuoteItem {
  const hasSources = item.sources && Object.keys(item.sources).length > 0;
  const sources = hasSources ? item.sources : { [MANUAL_SOURCE]: item.quantity };
  const summed = sumSources(sources);
  const quantity = clampQuantity(summed);

  if (hasSources && item.quantity === quantity && summed === quantity) return item;
  // When the clamp had to move the number, provenance can no longer add up, so
  // the difference is attributed to the manual source.
  if (summed === quantity) return { ...item, sources, quantity };
  return { ...item, sources: { [MANUAL_SOURCE]: quantity }, quantity };
}

export function normaliseItems(items: readonly QuoteItem[]): QuoteItem[] {
  return items.map(normaliseItem);
}

function withQuantityFromSources(item: QuoteItem, sources: ItemSources): QuoteItem {
  return { ...item, sources, quantity: sumSources(sources) };
}

function addToSource(sources: ItemSources, key: string, quantity: number): ItemSources {
  return { ...sources, [key]: (sources[key] ?? 0) + quantity };
}

export function createQuoteItem(
  service: Service,
  quantity: number,
  source: string = MANUAL_SOURCE,
): QuoteItem {
  const safe = clampQuantity(quantity);
  return {
    id: createId('item'),
    serviceId: service.id,
    sources: { [source]: safe },
    name: service.name,
    description: service.description,
    unit: service.unit,
    quantity: safe,
    unitPrice: service.price,
    taxable: service.isTaxable,
  };
}

export function createBlankItem(): QuoteItem {
  return {
    id: createId('item'),
    serviceId: null,
    sources: { [MANUAL_SOURCE]: MIN_ITEM_QUANTITY },
    name: '',
    unit: 'each',
    quantity: MIN_ITEM_QUANTITY,
    unitPrice: 0,
    taxable: true,
  };
}

/** Adds a catalog service, merging into an existing line for the same service. */
export function addService(
  items: readonly QuoteItem[],
  service: Service,
  quantity = MIN_ITEM_QUANTITY,
  source: string = MANUAL_SOURCE,
): QuoteItem[] {
  const safe = clampQuantity(quantity);
  const existing = items.find((item) => item.serviceId === service.id);
  if (!existing) return [...items, createQuoteItem(service, safe, source)];

  return items.map((item) =>
    item.id === existing.id
      ? withQuantityFromSources(item, addToSource(normaliseItem(item).sources, source, safe))
      : item,
  );
}

export interface ApplyPresetResult {
  items: QuoteItem[];
  /** Lines the bundle actually contributed to — skipped ones are unavailable. */
  appliedCount: number;
  /** Bundle entries whose service has been deleted or archived. */
  missingCount: number;
}

export function applyPreset(
  items: readonly QuoteItem[],
  preset: QuotePreset,
  services: readonly Service[],
): ApplyPresetResult {
  const byId = new Map(services.map((service) => [service.id, service]));
  let next = normaliseItems(items);
  let appliedCount = 0;
  let missingCount = 0;

  for (const presetItem of preset.items) {
    const service = byId.get(presetItem.serviceId);
    // A bundle survives its services being deleted — it just adds fewer lines.
    if (!service || service.status !== 'active') {
      missingCount += 1;
      continue;
    }
    next = addService(next, service, presetItem.quantity, preset.id);
    appliedCount += 1;
  }

  return { items: next, appliedCount, missingCount };
}

export interface RemovePresetResult {
  items: QuoteItem[];
  /** Lines dropped entirely. */
  removedCount: number;
  /** Lines that survived because another bundle or a manual add also owns them. */
  keptCount: number;
}

/**
 * Removes only what this bundle contributed. A line shared with another active
 * bundle keeps that bundle's share and stays on the quote.
 */
export function removePreset(items: readonly QuoteItem[], presetId: ID): RemovePresetResult {
  const next: QuoteItem[] = [];
  let removedCount = 0;
  let keptCount = 0;

  for (const raw of normaliseItems(items)) {
    if (!(presetId in raw.sources)) {
      next.push(raw);
      continue;
    }

    const { [presetId]: _removed, ...remaining } = raw.sources;
    const quantity = sumSources(remaining);

    if (quantity <= 0) {
      removedCount += 1;
      continue;
    }

    // A surviving line still owes the minimum: if the bundle's share was
    // fractional, what remains can land below 1 and must be lifted back.
    next.push(normaliseItem(withQuantityFromSources(raw, remaining)));
    keptCount += 1;
  }

  return { items: next, removedCount, keptCount };
}

/**
 * Applies a user-typed quantity while preserving provenance.
 *
 * Increasing a quantity is credited to the manual source. Decreasing below what
 * the bundles contributed scales their shares down proportionally, so removing
 * a bundle afterwards still subtracts a sensible amount.
 */
export function setItemQuantity(
  items: readonly QuoteItem[],
  itemId: ID,
  quantity: number,
): QuoteItem[] {
  const target = clampQuantity(quantity);

  return normaliseItems(items).map((item) => {
    if (item.id !== itemId) return item;

    const presetEntries = Object.entries(item.sources).filter(([key]) => key !== MANUAL_SOURCE);
    const presetTotal = presetEntries.reduce((total, [, value]) => total + value, 0);

    if (target >= presetTotal) {
      const sources: ItemSources = Object.fromEntries(presetEntries);
      const manual = target - presetTotal;
      if (manual > 0 || presetEntries.length === 0) sources[MANUAL_SOURCE] = manual;
      return { ...item, sources, quantity: target };
    }

    const scale = presetTotal === 0 ? 0 : target / presetTotal;
    const scaled: ItemSources = {};
    let running = 0;
    presetEntries.forEach(([key, value], index) => {
      const share = index === presetEntries.length - 1 ? target - running : value * scale;
      scaled[key] = Math.max(0, share);
      running += scaled[key] as number;
    });
    return { ...item, sources: scaled, quantity: target };
  });
}

export function activePresetIds(items: readonly QuoteItem[]): Set<ID> {
  const active = new Set<ID>();
  for (const item of normaliseItems(items)) {
    for (const [key, value] of Object.entries(item.sources)) {
      if (key !== MANUAL_SOURCE && value > 0) active.add(key);
    }
  }
  return active;
}

export function presetContributionCounts(items: readonly QuoteItem[]): Record<ID, number> {
  const counts: Record<ID, number> = {};
  for (const item of normaliseItems(items)) {
    for (const [key, value] of Object.entries(item.sources)) {
      if (key === MANUAL_SOURCE || value <= 0) continue;
      counts[key] = (counts[key] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * Catalog lines the user picked themselves rather than receiving from a bundle.
 * Kept for provenance reporting; the save-bundle gate deliberately does *not*
 * use it — see `catalogLineCount`.
 */
export function manualLineCount(items: readonly QuoteItem[]): number {
  return normaliseItems(items).filter(
    (item) => item.serviceId !== null && (item.sources[MANUAL_SOURCE] ?? 0) > 0,
  ).length;
}

/**
 * Unique catalog services on the quote, whatever their origin.
 *
 * This is what gates "save as a bundle": a combination assembled from two
 * existing bundles is exactly the sort of thing worth saving, so provenance is
 * intentionally ignored here. Custom ad-hoc lines have no `serviceId` and are
 * excluded, since a bundle can only reference catalog services.
 */
export function catalogLineCount(items: readonly QuoteItem[]): number {
  const unique = new Set(
    normaliseItems(items)
      .filter((item) => item.serviceId !== null)
      .map((item) => item.serviceId as string),
  );
  return unique.size;
}

/** The saved bundle this quote already is, if any — used to disable re-saving. */
export function findMatchingPreset(
  items: readonly QuoteItem[],
  presets: readonly QuotePreset[],
): QuotePreset | null {
  const catalogLines = normaliseItems(items).filter((item) => item.serviceId !== null);
  if (catalogLines.length === 0) return null;

  const signature = new Map(
    catalogLines.map((item) => [item.serviceId as string, item.quantity]),
  );

  return (
    presets.find((preset) => {
      if (preset.items.length !== signature.size) return false;
      return preset.items.every(
        (presetItem) => signature.get(presetItem.serviceId) === presetItem.quantity,
      );
    }) ?? null
  );
}
